/**
 * Intelligence — merges Story Threads + Synthesis into one page with two tabs.
 *
 * Threads tab: pattern detection across announcements (entity, activity, sector, macro)
 * Synthesis tab: AI-generated synthesis articles from threads
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GitBranch, Layers } from 'lucide-react';

// Re-export the existing page components as tabs
// This avoids rewriting 447 lines of working code
import { StoryThreadsPage } from './StoryThreadsPage.js';
import { SynthesisPage } from './SynthesisPage.js';

type Tab = 'threads' | 'synthesis';

export function IntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as Tab) || 'threads';
  const setActiveTab = (tab: Tab) => setSearchParams({ tab });

  const tabs: { key: Tab; label: string; icon: typeof GitBranch }[] = [
    { key: 'threads', label: 'Story Threads', icon: GitBranch },
    { key: 'synthesis', label: 'Synthesis', icon: Layers },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      <h1 className="text-2xl font-bold text-grey-900">Intelligence</h1>

      <div className="flex border-b border-grey-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'threads' && <StoryThreadsPage />}
      {activeTab === 'synthesis' && <SynthesisPage />}
    </div>
  );
}
